import React, { useEffect, useMemo, useState } from 'react';
import type { BrandKit } from '@goldcast/api/backend';
import { buildFontFaceMap, getFontEntriesFromPlatformBrandKit, injectStylesheetFonts } from './fontKitUtils';
import FontList from './FontList';
import type { PlatformFontEntry } from '@/player-config/src/types/brand-kit-types';
import BrandKitSelector from '@/Pages/BrandKit/components/BrandKitSelector/BrandKitSelector';
import { loadBrandKitFonts } from '@/Pages/BrandKit/components/utils';
import { FONTS_DEFAULT } from '@/player-config/src/constants/fonts';
import usePlatformBrandKits from '@/hooks/usePlatformBrandKits';
import { classnames } from '@/libs/utils';

type TabId = 'brand-kit' | 'custom';
const TABS: TabId[] = ['brand-kit', 'custom'];

export interface BrandKitFontPickerContentProps {
  selectedFontUrl: string;
  /** When several options share the same URL, disambiguate the active row. */
  selectedFontDisplayName?: string;
  /** Already-composed callback: calls onFontSelect and handles back-navigation or panel close. */
  onSelect: (fontUrl: string, fontDisplayName: string) => void;
}

export function BrandKitFontPickerContent({
  selectedFontUrl,
  selectedFontDisplayName,
  onSelect
}: BrandKitFontPickerContentProps) {
  const [activeTab, setActiveTab] = useState<TabId>('brand-kit');

  const { brandKits, selectedBrandKitId: globalSelectedKitId, ftuxFontEntries } = usePlatformBrandKits();
  const [localSelectedKitId, setLocalSelectedKitId] = useState<string | undefined>(globalSelectedKitId);

  useEffect(() => {
    setLocalSelectedKitId(globalSelectedKitId);
  }, [globalSelectedKitId]);

  const activeKit: BrandKit | undefined = useMemo(
    () => brandKits.find(kit => kit.id === (localSelectedKitId ?? globalSelectedKitId)) ?? brandKits[0],
    [brandKits, localSelectedKitId, globalSelectedKitId]
  );

  const platformFontEntries = useMemo(() => getFontEntriesFromPlatformBrandKit(activeKit), [activeKit]);

  const customFontEntries: PlatformFontEntry[] = useMemo(() => {
    const defaults = FONTS_DEFAULT.map(f => ({ url: f.url, name: f.name }));
    const defaultUrls = new Set(defaults.map(e => e.url));
    const uniqueFtux = ftuxFontEntries.filter(e => !defaultUrls.has(e.url));
    return [...defaults, ...uniqueFtux];
  }, [ftuxFontEntries]);

  // Pre-load fonts whenever this component is mounted (covers both inline and floating-panel cases).
  useEffect(() => {
    if (activeTab === 'brand-kit' && platformFontEntries.length) {
      loadBrandKitFonts(buildFontFaceMap(platformFontEntries));
      injectStylesheetFonts(platformFontEntries);
    } else if (activeTab === 'custom' && customFontEntries.length) {
      loadBrandKitFonts(buildFontFaceMap(customFontEntries));
    }
  }, [activeTab, platformFontEntries, customFontEntries]);

  return (
    <>
      <div className="mb-2 flex rounded-lg border-b border-slate-100 bg-slate-100 p-0.5">
        {TABS.map(tab => (
          <button
            key={tab}
            type="button"
            data-testid={`font-picker-tab-${tab}`}
            onClick={() => setActiveTab(tab)}
            className={classnames(
              'flex-1 rounded-md py-1.5 text-sm font-medium text-slate-900 transition-all',
              activeTab === tab ? 'bg-white shadow-sm' : 'text-slate-600'
            )}
          >
            {tab === 'brand-kit' ? 'Brand Kit' : 'Custom'}
          </button>
        ))}
      </div>

      {activeTab === 'brand-kit' && (
        <div>
          <div className="mb-1">
            <BrandKitSelector selectedBrandKitId={localSelectedKitId} onBrandKitChange={setLocalSelectedKitId} />
          </div>
          {platformFontEntries.length === 0 ? (
            <p className="py-4 text-center text-xs text-slate-400">No fonts in this brand kit</p>
          ) : (
            <div className="max-h-[180px] overflow-y-auto">
              <FontList
                data-testid="brand-kit-font-list"
                entries={platformFontEntries}
                selectedUrl={selectedFontUrl}
                selectedDisplayName={selectedFontDisplayName}
                onSelect={onSelect}
              />
            </div>
          )}
        </div>
      )}

      {activeTab === 'custom' && (
        <div className="max-h-[180px] overflow-y-auto">
          <FontList
            data-testid="custom-font-list"
            entries={customFontEntries}
            selectedUrl={selectedFontUrl}
            selectedDisplayName={selectedFontDisplayName}
            onSelect={onSelect}
          />
        </div>
      )}
    </>
  );
}
